import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { loadMembership, startCheckout } from "../../store/billingSlice";
import { isMembershipCurrent } from "../../utils/billing";
import { colors } from "../../styles/colors";
import { Button } from "../ui";
import { PREMIUM_FEATURES, UPGRADE_COPY, type UpgradeTrigger } from "../../config/upgrade";

interface UpgradeModalProps {
  open: boolean;
  trigger?: UpgradeTrigger;
  onClose: () => void;
}

const FEATURE_ICONS: Record<string, React.ReactNode> = {
  star: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M9 2L11.09 6.26L16 7L12.5 10.4L13.18 15L9 12.77L4.82 15L5.5 10.4L2 7L6.91 6.26L9 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
    </svg>
  ),
  target: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="9" r="7" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="9" cy="9" r="3.5" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="9" cy="9" r="1" fill="currentColor"/>
    </svg>
  ),
  sparkles: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M9 2V4M9 14V16M2 9H4M14 9H16M4.22 4.22L5.64 5.64M12.36 12.36L13.78 13.78M4.22 13.78L5.64 12.36M12.36 5.64L13.78 4.22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="9" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  ),
  calendar: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="2" y="3.5" width="14" height="13" rx="2" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M2 7.5H16" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M6 2V4M12 2V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  tag: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M2 2H8.5L16 9.5L9.5 16L2 8.5V2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <circle cx="5.5" cy="5.5" r="1" fill="currentColor"/>
    </svg>
  ),
};

export default function UpgradeModal({ open, trigger = "banner", onClose }: UpgradeModalProps) {
  const dispatch = useAppDispatch();
  const { membership, checkoutLoading: loading, loading: verifying, error } = useAppSelector((state) => state.billing);
  const [billingInterval, setBillingInterval] = useState<"monthly" | "annual">("monthly");
  const copy = UPGRADE_COPY[trigger];
  const ready = isMembershipCurrent(membership);
  const canPurchase = ready && membership?.canPurchase === true;

  useEffect(() => { if (open) dispatch(loadMembership(undefined)); }, [dispatch, open]);

  if (!open) return null;

  const handleClose = () => { if (!loading) onClose(); };
  const handleUpgrade = async () => {
    if (!canPurchase) return;
    const result = await dispatch(startCheckout(billingInterval));
    if (startCheckout.fulfilled.match(result)) onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[1000] bg-black/40 backdrop-blur-[2px]"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal — full-width bottom sheet on mobile, centered on md+ */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgrade-modal-title"
        data-testid="upgrade-modal"
        className="fixed bottom-0 left-0 right-0 z-[1001] w-full rounded-t-2xl bg-white shadow-2xl md:bottom-auto md:left-1/2 md:top-1/2 md:w-full md:max-w-md md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-2xl"
        style={{ border: `1px solid ${colors.borderLight}` }}
      >
        {/* Header gradient band */}
        <div
          className="flex items-start justify-between rounded-t-2xl px-6 pt-6 pb-5"
          style={{ background: "linear-gradient(135deg, #f0f7ff 0%, #fafbff 100%)" }}
        >
          <div>
            {/* Premium badge */}
            <span
              className="mb-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider"
              style={{ background: colors.brandPrimary, color: "#fff" }}
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M5 1L6.18 3.4L9 3.82L7 5.77L7.45 8.59L5 7.3L2.55 8.59L3 5.77L1 3.82L3.82 3.4L5 1Z" fill="currentColor"/>
              </svg>
              Premium
            </span>
            <h2
              id="upgrade-modal-title"
              className="m-0 text-[20px] font-bold leading-snug"
              style={{ color: colors.textPrimary }}
            >
              {copy.headline}
            </h2>
            <p className="m-0 mt-1.5 text-[13px] leading-relaxed" style={{ color: colors.textTertiary }}>
              {copy.subtext}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="ml-4 mt-0.5 flex h-7 w-7 flex-shrink-0 cursor-pointer items-center justify-center rounded-lg border-0 bg-transparent transition-colors hover:bg-slate-100"
            aria-label="Close"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 2L12 12M12 2L2 12" stroke={colors.textTertiary} strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Feature list */}
        <div className="px-6 py-3">
          <ul className="m-0 flex flex-col gap-2 p-0 list-none">
            {PREMIUM_FEATURES.map((feature) => (
              <li key={feature.title} className="flex items-center gap-3">
                <span
                  className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg"
                  style={{ background: `${colors.brandPrimary}15`, color: colors.brandPrimary }}
                >
                  {FEATURE_ICONS[feature.icon]}
                </span>
                <div className="min-w-0">
                  <span className="text-[13px] font-semibold" style={{ color: colors.textPrimary }}>
                    {feature.title}
                  </span>
                  <span className="ml-1.5 text-[12px]" style={{ color: colors.textTertiary }}>
                    — {feature.description}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* CTA footer */}
        <div
          className="flex flex-col gap-3 rounded-b-2xl px-6 py-4"
          style={{ borderTop: `1px solid ${colors.borderLight}` }}
        >
          {/* Billing interval toggle */}
          <div className="flex items-center rounded-xl p-1" style={{ background: "#f1f5f9" }}>
            <button
              type="button"
              onClick={() => setBillingInterval("monthly")}
              className="flex-1 rounded-lg py-1.5 text-[13px] font-semibold transition-colors border-0 cursor-pointer"
              style={{
                background: billingInterval === "monthly" ? "#fff" : "transparent",
                color: billingInterval === "monthly" ? colors.textPrimary : colors.textTertiary,
                boxShadow: billingInterval === "monthly" ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
              }}
            >
              Monthly billing
            </button>
            <button
              type="button"
              onClick={() => setBillingInterval("annual")}
              className="flex-1 rounded-lg py-1.5 text-[13px] font-semibold transition-colors border-0 cursor-pointer"
              style={{
                background: billingInterval === "annual" ? "#fff" : "transparent",
                color: billingInterval === "annual" ? colors.textPrimary : colors.textTertiary,
                boxShadow: billingInterval === "annual" ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
              }}
            >
              Annual billing
            </button>
          </div>
          <p className="text-xs text-slate-600">The current price, currency and renewal terms are shown at secure checkout before you confirm payment.</p>
          {!canPurchase && <p role="status" className="text-sm text-slate-700">{verifying ? "Verifying membership…" : membership?.premium
            ? "Premium is already active. Manage the existing subscription in Settings."
            : "Verify your membership or manage existing billing in Settings. New purchases may not be enabled yet."}</p>}
          {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
          <Button data-testid="upgrade-membership-refresh" variant="ghost" onClick={() => dispatch(loadMembership({ force: true }))} disabled={verifying || loading}>Refresh Membership</Button>
          <Button
            data-testid="start-premium-checkout"
            variant="primary"
            size="md"
            block
            loading={loading}
            disabled={!canPurchase || verifying}
            onClick={handleUpgrade}
          >
            Continue to Secure Checkout
          </Button>
          <button
            type="button"
            onClick={handleClose}
            className="cursor-pointer border-0 bg-transparent py-1 text-[12px] transition-colors"
            style={{ color: colors.textTertiary }}
          >
            Maybe later
          </button>
        </div>
      </div>
    </>
  );
}
